import { Attributes, List, LookupBase, NeoModel } from '@singularsystems/neo-core';
import PortfolioSettingsLookup from './PortfolioSettingsLookup';
import PortfolioIndicators from './PortfolioIndicators';
import PortfolioBalanceLookup from './PortfolioBalanceLookup';
import CurrencyLookup from 'st-common/Transactions/Models/CurrencyLookup';
import ExchangeRateLookup from 'st-common/Transactions/Models/ExchangeRateLookup';
import { IncentiveSchemeLookup } from 'st-awards';
import InstrumentLookup from 'st-common/Transactions/Models/InstrumentLookup';
import TradeRequestLookup from '../Trading/Lookups/TradeRequestLookup';
import { SecondmentLookup } from 'st-participants';
import { AppService, Types } from '../../TransactionsTypes';
import TradingRestrictionLookup from "../Trading/Restrictions/TradingRestrictionLookup";

@NeoModel
export default class AppDataLookup extends LookupBase {

    @Attributes.ChildObject(PortfolioSettingsLookup)
    public settings: PortfolioSettingsLookup = new PortfolioSettingsLookup();

    @Attributes.ChildObject(PortfolioIndicators)
    public portfolioIndicators: PortfolioIndicators = new PortfolioIndicators();

    public trancheBalances = new List(PortfolioBalanceLookup);

    /**
     * List of currencies from the transactions service.
     */
    private currencies = new List(CurrencyLookup);

    public mainCurrencies: CurrencyLookup[] = [];

    public exchangeRates = new List(ExchangeRateLookup);

    @Attributes.Observable()
    public pendingTradeRequests = new List(TradeRequestLookup);

    public participantSecondments = new List(SecondmentLookup);

    @Attributes.ChildList(TradingRestrictionLookup)
    public closedPeriods: TradingRestrictionLookup[] = [];

    public get paymentCountryList() {
        const countryList = this.participantSecondments
            .groupBy(c => c.countryId)
            .map(g => ({ countryId: g.item.countryId, countryName: g.item.countryName }));

        if (!countryList.find(c => c.countryId === this.settings.residentCountryId)) {
            countryList.push({
                countryId: this.settings.residentCountryId,
                countryName: this.settings.residentCountryName
            });
        }
        return countryList;
    }
    // Client only properties / methods

    public pendingTradeRequestsLoaded = false;

    public instruments: InstrumentLookup[] = [];

    @Attributes.Observable()
    private _instrumentEditMode = false;

    public get instrumentEditMode() {
        return this._instrumentEditMode;
    }
    public set instrumentEditMode(value: boolean) {
        this._instrumentEditMode = value;
        this.instruments.forEach(c => c.customPrice = value ? c.price : null);
    }

    public incentiveSchemes: IncentiveSchemeLookup[] = [];

    public hasCostsAlreadyPaidGroups : boolean = false;

    public ownedInstruments: InstrumentLookup[] = [];

    public get hasUnvested() {
        return this.trancheBalances.filter(c => !c.isVested).length > 0;
    }

    public get hasVested() {
        return this.trancheBalances.filter(c => c.isVested && c.availableBalance > 0).length > 0;
    }

    public setup(instruments: InstrumentLookup[], incentiveSchemes: IncentiveSchemeLookup[]) {

        this.mainCurrencies = this.currencies.filter(c => c.isMain);

        if (this.settings.taxPercentage === null) {
            this.settings.taxPercentage = 0;
            this.settings.missingTaxRate = true;
        }

        this.incentiveSchemes = incentiveSchemes;
        this.instruments = instruments;

        const balanceByInstrument = this.trancheBalances.groupBy(c => c.instrumentId);
        const balanceByScheme = this.trancheBalances.groupBy(c => c.incentiveSchemeId);

        for (let byInstrument of balanceByInstrument) {
            const instrumentLookup = instruments.find(c => c.instrumentCode === byInstrument.item.instrumentCode)!;

            for (let tranche of byInstrument.details) {
                tranche.instrument = instrumentLookup;
                tranche.settings = this.settings;
                tranche.incentiveScheme = incentiveSchemes.find(c => c.incentiveSchemeId === tranche.incentiveSchemeId)!;

                if (tranche.incentiveScheme.groupCostsAlreadyPaid) {
                    this.hasCostsAlreadyPaidGroups = true;
                }

                // Check to see if the list is not null and contains elements
                if (this.settings.participantIncentiveSchemeTypeTaxRates != null && this.settings.participantIncentiveSchemeTypeTaxRates.length > 0) {
                    let customIncentiveSchemeTypeTaxRate = this.settings.participantIncentiveSchemeTypeTaxRates.find(c => c.incentiveSchemeTypeId === tranche.incentiveScheme.incentiveSchemeTypeId)?.taxRate;
                    tranche.taxRate = customIncentiveSchemeTypeTaxRate ?? this.settings.taxPercentageOrDefault;
                } else {
                    tranche.taxRate = this.settings.taxPercentageOrDefault;
                }

            }

            const price = byInstrument.details.find(c => c.fixedPrice === null);
            if (price) {
                instrumentLookup.price = price.instrumentPrice;
            }
        }

        for (let tranche of this.trancheBalances.filter(c => !!c.trackingInstrumentCode)) {
            const trackingInstrumentLookup = instruments.find(c => c.instrumentCode === tranche.trackingInstrumentCode);
            if (trackingInstrumentLookup) {
                tranche.trackingInstrument = trackingInstrumentLookup;
                trackingInstrumentLookup.price = tranche.trackingInstrumentPrice;
            }
        }

        for (let tranche of this.trancheBalances.filter(c => !!c.settlementInstrumentCode)) {
            const settlementInstrumentLookup = instruments.find(c => c.instrumentCode === tranche.settlementInstrumentCode);
            if (settlementInstrumentLookup) {
                tranche.settlementInstrument = settlementInstrumentLookup;
                settlementInstrumentLookup.price = tranche.settlementInstrumentPrice;
            }
        }

        const balanceByTrackingInstrument = this.trancheBalances.filter(c => !!c.trackingInstrumentCode).groupBy(c => c.trackingInstrumentCode);

        this.ownedInstruments = balanceByInstrument.map(c => c.item.instrument)
            .concat(balanceByTrackingInstrument.map(c => c.item.trackingInstrument!))
            .groupBy(c => c.instrumentCode, (k, i, d) => i)
            .sortBy(c => c.instrumentCode);

        this.setupLinkedTranches();

        // TODO: remove when fixed in neo
        // @ts-ignore
        this.closedPeriods = [...List.fromJSArray(TradingRestrictionLookup, this.closedPeriods)];

        // Remove closed periods that don't apply.
        this.closedPeriods = TradingRestrictionLookup.filterForInstrumentAndSchemes(this.closedPeriods, 
            balanceByInstrument.map(c => c.item.instrumentId), 
            balanceByScheme.map(c => c.item.incentiveSchemeId));

    }

    private setupLinkedTranches() {
        const byAwardLink = this.trancheBalances.filter(c => c.awardLinkId !== null).groupBy(c => `${c.awardLinkId!}.${c.vestingDate.getTime()}`);
        for (let awardLink of byAwardLink) {
            for (let tranche of awardLink.details) {
                tranche.linkedTranches = [];
            }
            awardLink.item.linkedTranches = awardLink.details;
        }
    }

    public setupPendingTradeRequests() {
        this.populateTradeRequests(this.pendingTradeRequests);
        this.pendingTradeRequestsLoaded = true;
    }

    public populateTradeRequests(tradeRequests: TradeRequestLookup[]) {
        tradeRequests.forEach(tr => {
            tr.settlementRequiresTaxDirective = this.settings.settlementRequiresTaxDirective;
            tr.showDetailedStatusesOnTradeHistory = this.settings.showDetailedStatusesOnTradeHistory
            tr.incentiveSchemeName = this.incentiveSchemes.find(c => c.incentiveSchemeId === tr.incentiveSchemeId)?.incentiveSchemeName ?? "";
            tr.currencySymbol = this.instruments.find(c => c.instrumentCode === tr.instrumentCode)?.currencySymbol ?? "";

            for (let tranche of tr.tranches) {
                tranche.trancheBalance = this.trancheBalances.find(c => tranche.trancheId === c.trancheId) ?? null;
            }
            tr.hasTrancheData = tr.tranches.length > 0 && tr.tranches.every(c => c.trancheBalance !== null);
        });
    }

    public setInstrumentRates(currency: CurrencyLookup, isSelected: boolean) {
        let hasMissingExchangeRates = false;

        // Instruments
        for (let instrument of this.ownedInstruments) {

            const instrumentCurrencyId = this.currencies.find(c => c.currencyCode === instrument.currencyCode)?.currencyId ?? 0;
            if (instrumentCurrencyId === currency.currencyId) {
                instrument.lastRate = 1;
            } else {
                const exchangeRate = this.exchangeRates.find(c => c.fromCurrencyId === instrumentCurrencyId);
                if (exchangeRate) {
                    instrument.lastRate = exchangeRate.rate;
                } else {
                    instrument.lastRate = null;
                    hasMissingExchangeRates = true;
                }
            }

            if (isSelected) {
                instrument.conversionCurrency = currency;
                instrument.rate = instrument.lastRate;
            } else {
                instrument.rate = null;
            }
        }

        return hasMissingExchangeRates;
    }

    public async fetchSecondmentData() {
        if (!this.participantSecondments || this.participantSecondments.length === 0) {
            if (!this.settings.secondmentConfirmation) {
                const taskRunner = AppService.get(Types.Neo.TaskRunner)
                const participantsApiClient = AppService.get(Types.Participants.ApiClients.MainApiClient)

                const results = await taskRunner.waitForAll({
                    participantSecondments: participantsApiClient.getSecondments(),
                });
                this.participantSecondments.set(results.participantSecondments.data);
            }
        }
        return this.participantSecondments;
    }
}
